import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import "./ApiPostGet.css";
import ModuleSidebar from "../../components/ModuleSidebar/ModuleSidebar";
import { TbFolders, TbNews, TbEdit, TbFolder, TbFileInvoice } from "react-icons/tb";
import { useLanguage } from "../../LanguageProvider";
import Dropdown from "../../components/Dropdown/Dropdown";
import InputField from "../../components/InputField/InputField";
import SendButton from "./components/SendButton/SendButton";
import { TbDeviceFloppy, TbDotsVertical, TbPlus } from "react-icons/tb";
import Authorization from "./components/Authorization/Authorization";
import Headers from "./components/Headers/Headers";
import Body from "./components/Body/Body"; // Assuming this is your custom component
import { GeneralContext } from "../../GeneralProvider";
import axios from "axios";
import AddCollectionModal from "./components/AddCollectionModal/AddCollectionModal";
import AddRequestModal from "./components/AddRequestModal/AddRequestModal";
import { useToaster } from "../../ToasterProvider";
import AddApiGroupModal from "./components/AddApiGroupModal/AddApiGroupModal";
import TabsHolder from "./components/TabsHolder/TabsHolder";


function getBaseUrl(fullUrl) {
  try {
    const url = new URL(fullUrl);
    return `${url.protocol}//${url.host}`;
  } catch (error) {
    console.error("Invalid URL provided:", error);
    return null;
  }
}
function getEndPoint(fullUrl) {
  try {
    const url = new URL(fullUrl);
    return url.pathname;
  } catch (error) {
    console.error("Invalid URL provided:", error);
    return null;
  }
}

// == Postman import helpers ==

// Postman URL -> düz string
function mapPostmanUrl(url) {
  // URL hiç yoksa kullanmayalım
  if (!url) return null;

  // Düz string ise, boş stringleri de ele
  if (typeof url === "string") {
    const trimmed = url.trim();
    if (!trimmed) return null;
    return trimmed;
  }

  // raw alanı varsa onu kullan
  if (url.raw && typeof url.raw === "string") {
    const trimmed = url.raw.trim();
    if (!trimmed) return null;
    return trimmed;
  }

  const protocol = url.protocol || "https";
  const host = Array.isArray(url.host)
    ? url.host.join(".")
    : (url.host || "").toString().trim();
  const path = Array.isArray(url.path)
    ? `/${url.path.join("/")}`
    : (url.path || "");
  const query =
    Array.isArray(url.query) && url.query.length
      ? "?" +
      url.query
        .map((q) =>
          `${encodeURIComponent(q.key)}=${encodeURIComponent(q.value ?? "")}`
        )
        .join("&")
      : "";

  // Host yoksa geçerli bir URL yok, null dön
  if (!host) return null;

  return `${protocol}://${host}${path}${query}`;
}

// Postman body -> string (JSON string ya da raw text)
function mapPostmanBody(request) {
  const body = request?.body;
  if (!body) return JSON.stringify({}, null, 2);

  if (body.mode === "raw") {
    const raw = body.raw || "";
    // JSON olup olmadığını deneriz; JSON ise pretty-print, değilse olduğu gibi bırak.
    try {
      const parsed = JSON.parse(raw);
      return JSON.stringify(parsed, null, 2);
    } catch (_) {
      return raw;
    }
  }

  if (body.mode === "urlencoded" && Array.isArray(body.urlencoded)) {
    const obj = {};
    body.urlencoded.forEach((p) => {
      if (p?.key) obj[p.key] = p.value ?? "";
    });
    return JSON.stringify(obj, null, 2);
  }

  if (body.mode === "formdata" && Array.isArray(body.formdata)) {
    const obj = {};
    body.formdata.forEach((p) => {
      if (p?.key) obj[p.key] = p.value ?? "";
    });
    return JSON.stringify(obj, null, 2);
  }

  // Diğer modlar için şimdilik boş obje
  return JSON.stringify({}, null, 2);
}

// Postman auth -> senin auth formatın
function mapPostmanAuth(item) {
  const auth = item?.request?.auth;
  if (!auth || !auth.type) {
    return { type: "no-auth" };
  }

  const type = auth.type;

  if (type === "bearer" && Array.isArray(auth.bearer)) {
    const tokenObj = auth.bearer.find((b) => b.key === "token") || auth.bearer[0];
    const token = tokenObj?.value || "";
    return { type: "Bearer", token };
  }

  if (type === "basic" && Array.isArray(auth.basic)) {
    const username = auth.basic.find((b) => b.key === "username")?.value || "";
    const password = auth.basic.find((b) => b.key === "password")?.value || "";
    return { type: "basic-auth", credentials: { username, password } };
  }

  return { type: "no-auth" };
}

// Postman header -> senin headers formatın
function mapPostmanHeaders(item) {
  const headers = item?.request?.header;
  if (!Array.isArray(headers)) return [];

  return headers.map((h) => ({
    key: h?.key || "",
    value: h?.value || "",
    description: h?.description || "",
  }));
}

// Tek bir Postman item'ını (request) senin API objene çevir
function mapPostmanItemToApi(item, collectionId) {
  const name = item?.name || "Imported Request";
  const method = item?.request?.method?.toLowerCase?.() || "get";
  const url = mapPostmanUrl(item?.request?.url);

  // 🚫 URL yoksa bu item'ı hiç oluşturma
  if (!url) {
    console.warn("URL bulunamadığı için Postman item import edilmedi:", name);
    return null;
  }

  const auth = mapPostmanAuth(item);
  const headers = mapPostmanHeaders(item);
  const body = mapPostmanBody(item.request);

  return {
    apiTitle: name,
    method,
    url,
    auth,
    headers,
    body,
    baseUrl: getBaseUrl(url),
    endpoint: getEndPoint(url),
    documentationName: name,
    collectionId,
  };
}

// Collection içindeki tüm request'leri (folder vs fark etmeksizin) düz listeye çevir
function flattenPostmanItems(items, acc = []) {
  if (!Array.isArray(items)) return acc;

  items.forEach((it) => {
    if (it.request) {
      acc.push(it);
    } else if (Array.isArray(it.item)) {
      flattenPostmanItems(it.item, acc);
    }
  });

  return acc;
}

// Postman item'larını klasör PATH bilgisiyle birlikte toplayalım
// Sonuç: [{ item, folders: ['Parent', 'Child', ...] }, ...]
function collectPostmanItemsWithPath(items, parentFolders = [], acc = []) {
  if (!Array.isArray(items)) return acc;

  items.forEach((it) => {
    // Request ise
    if (it.request) {
      acc.push({
        item: it,
        folders: parentFolders, // ['Parent', 'Child']
      });
    }
    // Folder ise (altında item var)
    else if (Array.isArray(it.item)) {
      const folderName = it.name || "Folder";
      const nextPath = [...parentFolders, folderName];
      collectPostmanItemsWithPath(it.item, nextPath, acc);
    }
  });

  return acc;
}

// Function to remove comments from JSON string
function removeComments(jsonString) {
  if (typeof jsonString !== 'string') {
    return jsonString;
  }
  // This regex removes single-line comments (// ...) and multi-line comments (/* ... */)
  // It handles comments within strings correctly by using a placeholder approach.
  // Note: This is a simplified version and might not cover all edge cases.
  let inString = false;
  let inSingleLineComment = false;
  let inMultiLineComment = false;
  let result = '';
  let i = 0;

  while (i < jsonString.length) {
    const char = jsonString[i];
    const nextChar = jsonString[i + 1];

    if (inSingleLineComment) {
      if (char === '\n' || char === '\r') {
        inSingleLineComment = false;
        result += char; // Keep the newline character
      }
    } else if (inMultiLineComment) {
      if (char === '*' && nextChar === '/') {
        inMultiLineComment = false;
        i++; // Skip '/'
      }
    } else if (inString) {
      if (char === '\\' && nextChar) {
        result += char + nextChar;
        i++; // Skip next char as it's escaped
      } else if (char === '"') {
        inString = false;
        result += char;
      } else {
        result += char;
      }
    } else {
      if (char === '/' && nextChar === '/') {
        inSingleLineComment = true;
        i++; // Skip '/'
      } else if (char === '/' && nextChar === '*') {
        inMultiLineComment = true;
        i++; // Skip '*'
      } else if (char === '"') {
        inString = true;
        result += char;
      } else {
        result += char;
      }
    }
    i++;
  }
  return result;
}

// Function to auto-close brackets/quotes
function autoCloseBracketsAndQuotes(code, cursorPosition, char) {
  const pairs = {
    '{': '}',
    '[': ']',
    '(': ')',
    '"': '"',
    "'": "'",
    '`': '`',
  };

  const closingPairs = {
    '}': '{',
    ']': '[',
    ')': '(',
    '"': '"',
    "'": "'",
    '`': '`',
  };

  let newCode = code;
  let newCursorPosition = cursorPosition;

  if (pairs[char]) {
    // Opening bracket/quote
    newCode = code.slice(0, cursorPosition) + char + pairs[char] + code.slice(cursorPosition);
    newCursorPosition = cursorPosition + 1; // Move cursor after the opening character
  } else if (closingPairs[char]) {
    // Closing bracket/quote
    const expectedOpening = closingPairs[char];
    const beforeCursor = code.slice(0, cursorPosition);
    const lastOpeningIndex = beforeCursor.lastIndexOf(expectedOpening);

    if (lastOpeningIndex !== -1) {
      const afterLastOpening = beforeCursor.slice(lastOpeningIndex + 1);
      const unmatchedClosings = afterLastOpening.split('').filter(c => c === char).length;
      if (unmatchedClosings === 0) {
        // If the character at cursor is the same closing char, just move cursor forward
        if (code[cursorPosition] === char) {
          newCursorPosition = cursorPosition + 1;
          return { code: newCode, cursorPosition: newCursorPosition };
        }
      }
    }
    // Default behavior: insert the character as is
    newCode = code.slice(0, cursorPosition) + char + code.slice(cursorPosition);
    newCursorPosition = cursorPosition + 1;
  } else {
    // Any other character, just insert normally
    newCode = code.slice(0, cursorPosition) + char + code.slice(cursorPosition);
    newCursorPosition = cursorPosition + 1;
  }

  return { code: newCode, cursorPosition: newCursorPosition };
}


export default function ApiPostGet({ openModal }) {
  const { toastWarning } = useToaster();
  const { getTranslation } = useLanguage();
  const {
    handleAddApi,
    collections,
    bearerToken,
    handleBearerTokenChange,
    handleUpdateApi,
    handleSaveAsApi,
    selectedApi,
    setSelectedApi,
    handleSelectApi,
    handleDeleteApi,
    handleAddNewCollection,
    handleGetDocumentation,
    setSelectedDocumentationID,
    openedWindows,
    handleDeleteCollection,
    handleUpdateCollectionTitle,
    selectedProjectID,
    getUserAuthorizations,
    handleChangeApiGroup,
    handleCreateApiGroup,
    handleUpdateApiGroup,
    handleDeleteApiGroup,
    handleUpdateApiTitle,
    setCreateProjectLoading,
    getAllCollections
  } = useContext(GeneralContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddCollectionModalOpen, setIsAddCollectionModalOpen] =
    useState(false);
  const [isAddRequestModalOpen, setIsAddRequestModalOpen] = useState(false);
  const [isCreateGroupModalOpen, setIsCreateGroupodalOpen] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [requiredCollectionIdForCreateApi, setRequiredCollectionIdForCreateApi] = useState("");
  const [requiredCollectionIdForCreateGroup, setRequiredCollectionIdForCreateGroup] = useState("");
  const [selectedApiId, setSelectedApiId] = useState("");
  const [selectedSubmenu, setSelectedSubMenu] = useState("");
  const [apiTitle, setApiTitle] = useState(getTranslation("newApi"));
  const [selectedMethod, setSelectedMethod] = useState("get");
  const [url, setUrl] = useState("");
  const [activeTab, setActiveTab] = useState("authorization");
  const [selectedAuthOption, setSelectedAuthOption] = useState("no-auth");
  const [basicAuth, setBasicAuth] = useState({ username: "", password: "" });
  const [headers, setHeaders] = useState([
    { key: "", value: "", description: "" },
  ]);
  const [code, setCode] = useState(JSON.stringify({}, null, 2));
  const [resultsByApi, setResultsByApi] = useState({});
  const [result, setResult] = useState();
  const [menuOpen, setMenuOpen] = useState(false);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const [projectAuthorization, setProjectAuthorization] = useState(null);
  const [temporaryApis, setTemporaryApis] = useState([]);
  const [isNewApi, setIsNewApi] = useState(false);
  const [selectedNewApi, setSelectedNewApi] = useState();
  const [modifiedApis, setModifiedApis] = useState({});
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [openedTabs, setOpenedTabs] = useState([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerRef = useRef(null);
  const startRef = useRef(0);
  const menuRef = useRef();
  const abortRef = useRef(null);
  const textareaRef = useRef(null); // Ref for the textarea in Body component
  const fileInputRef = useRef(null); // Postman import için

  // Postman JSON dosyası seçildiğinde tetiklenen handler
  const handleImportPostmanFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      await importFromPostmanJson(json);
    } catch (error) {
      console.error("Postman import error:", error);
      toastWarning(getTranslation("invalidPostmanFile") || "Geçersiz Postman dosyası.");
    } finally {
      // Aynı dosyayı tekrar seçebilmek için input'u resetle
      event.target.value = "";
    }
  };

  // Postman JSON -> collection / tek api olarak içeri al
  const importFromPostmanJson = async (json) => {
    // Global loading aç
    setCreateProjectLoading(true);

    try {
      const isCollection = Array.isArray(json?.item);
      const isSingleRequest = !isCollection && json?.request;

      if (!isCollection && !isSingleRequest) {
        toastWarning(
          getTranslation("invalidPostmanFile") || "Geçersiz Postman JSON dosyası."
        );
        return;
      }

      let targetCollectionId = "";
      let createdCollection = null;

      // 🔹 1) POSTMAN COLLECTION İSE: HER ZAMAN YENİ COLLECTION AÇ
      if (isCollection) {
        const baseName =
          json?.info?.name ||
          json?.name ||
          getTranslation("importedPostmanCollection") ||
          "Imported from Postman";

        const uniqueTitle = `${baseName}`;

        createdCollection = await handleAddNewCollection(uniqueTitle);
        targetCollectionId =
          createdCollection?.collectionId || createdCollection?.id;

        if (!targetCollectionId) {
          console.error(
            "Collection ID alınamadı, handleAddNewCollection sonucu:",
            createdCollection
          );
          toastWarning(
            "Collection ID alınamadı. handleAddNewCollection geri dönüşünü kontrol et."
          );
          return;
        }
      }

      // 🔹 2) TEK REQUEST İSE: ÖNCE SEÇİLİ COLLECTION'A, YOKSA YENİ COLLECTION'A
      if (isSingleRequest) {
        const selectedCollection = collections?.find(
          (c) =>
            c.collectionId === selectedCollectionId ||
            c.id === selectedCollectionId
        );

        if (selectedCollection) {
          // seçili collection'a ekle
          targetCollectionId =
            selectedCollection.collectionId || selectedCollection.id;
        } else {
          // seçili yoksa yeni collection oluştur
          const baseName =
            json?.name ||
            getTranslation("importedPostmanRequest") ||
            "Imported Request";

          const uniqueTitle = `${baseName}`;

          createdCollection = await handleAddNewCollection(uniqueTitle);
          targetCollectionId =
            createdCollection?.collectionId || createdCollection?.id;

          if (!targetCollectionId) {
            console.error(
              "Collection ID alınamadı (single request), handleAddNewCollection sonucu:",
              createdCollection
            );
            toastWarning(
              "Collection ID alınamadı. handleAddNewCollection geri dönüşünü kontrol et."
            );
            return;
          }
        }
      }

      const createdApis = [];

      if (isCollection) {
        // Klasör path'li item listesi
        const entries = collectPostmanItemsWithPath(json.item);

        // Aynı folder path için tekrar tekrar grup yaratmamak için cache
        const groupIdByPath = {};

        for (const entry of entries) {
          const { item, folders } = entry;

          // Normal Postman item -> senin API DTO'n
          const apiDto = mapPostmanItemToApi(item, targetCollectionId);

          if (!apiDto || !apiDto.url) {
            continue;
          }

          try {
            // Önce API'yi oluştur (refresh = false)
            const created = await handleAddApi(
              apiDto,
              "",
              targetCollectionId,
              {
                skipCollectionsRefresh: true,
                suppressToast: true,
              }
            );
            if (!created) continue;

            createdApis.push(created);

            // Root seviyede ise (klasör yok) direkt ungroup'ta kalsın
            if (!folders || folders.length === 0) {
              continue;
            }

            // Folder path: ['Parent', 'Child'] -> "Parent / Child"
            const pathTitle = folders.join(" / ");

            // Bu path için daha önce grup oluşturduysak cache'den al
            if (!groupIdByPath[pathTitle]) {
              const createdGroup = await handleCreateApiGroup(
                pathTitle,
                selectedProjectID,
                targetCollectionId,
                {
                  skipCollectionsRefresh: true,
                  suppressToast: true,
                }
              );

              if (createdGroup && (createdGroup.groupId || createdGroup.id)) {
                groupIdByPath[pathTitle] =
                  createdGroup.groupId || createdGroup.id;
              } else {
                console.warn(
                  "Group ID alınamadı, path:",
                  pathTitle,
                  "createdGroup:",
                  createdGroup
                );
              }
            }

            const groupId = groupIdByPath[pathTitle];

            // Grup ID varsa, bu API'yi o gruba taşı
            if (groupId) {
              try {
                await handleChangeApiGroup(
                  targetCollectionId,
                  created.apiId,
                  groupId,
                  {
                    skipCollectionsRefresh: true,
                    suppressToast: true,
                  }
                );
              } catch (moveErr) {
                console.error(
                  "API'yi gruba taşıma hatası:",
                  moveErr,
                  "API:",
                  created,
                  "GroupId:",
                  groupId
                );
              }
            }
          } catch (err) {
            console.error("API import error:", err, "Item:", item);
          }
        }
      } else if (isSingleRequest) {
        const apiDto = mapPostmanItemToApi(json, targetCollectionId);

        if (!apiDto || !apiDto.url) {
          toastWarning(
            getTranslation("noRequestImported") ||
            "URL bulunmadığı için istek import edilmedi."
          );
          return;
        }

        try {
          const created = await handleAddApi(
            apiDto,
            "",
            targetCollectionId,
            {
              skipCollectionsRefresh: true,
              suppressToast: true,
            }
          );
          if (created) {
            createdApis.push(created);
          }
        } catch (err) {
          console.error("Single request import error:", err);
        }
      }

      if (!createdApis.length) {
        toastWarning(
          getTranslation("noRequestImported") ||
          "Hiç istek içeri aktarılamadı."
        );
        return;
      }

      // 🔹 Tüm import bittikten sonra TEK SEFERDE collections’ı yenile
      await getAllCollections();

      // İlk import edilen API'yi otomatik açalım
      const first = createdApis[0];
      try {
        handleSubmenuClick(first);
      } catch (e) {
        console.warn("handleSubmenuClick sırasında hata:", e);
      }
    } finally {
      // 🔹 Ne olursa olsun loading’i kapat
      setCreateProjectLoading(false);
    }
  };

  const switchToTab = (id) => {
    const tab = openedTabs.find(t => t.id === id);
    if (!tab) return;
    const apiLike = findApiByIdFromEverywhere(id);
    if (apiLike) handleSubmenuClick(apiLike);
  };

  // Tab kapatma
  const closeTab = (id) => {
    setOpenedTabs(prev => {
      const idx = prev.findIndex(t => t.id === id);
      if (idx === -1) return prev;
      const next = [...prev];
      next.splice(idx, 1);
      if (selectedApiId === id) {
        const fallback = next[idx - 1] || next[idx] || null;
        if (fallback) {
          switchToTab(fallback.id);
        } else {
          clearStates();
        }
      }
      return next;
    });
  };
  const findApiByIdFromEverywhere = (id) => {
    const temp = temporaryApis.find(t => t.apiId === id || t.id === id);
    if (temp) return { ...temp, apiId: temp.apiId ?? temp.id, isTemporary: true };
    const base = modifiedApis[id] || originalApiMap[id];
    if (base) return { ...base, apiId: id, isTemporary: false };
    return null;
  };

  // Tab aç (varsa öne getir)
  const openTabForApi = (api) => {
    const id = api.apiId || api.id;
    setOpenedTabs(prev => {
      if (prev.some(t => t.id === id)) return prev;
      const title = api.apiTitle || api.title || getTranslation("newApi");
      return [...prev, {
        id,
        title,
        collectionId: api.collectionId,
        isTemporary: !!api.isTemporary,
        groupId: api.groupId ?? null,
      }];
    });
  };
  const promoteTempTab = (tempId, created) => {
    const safeTitle =
      (created?.apiTitle && String(created.apiTitle).trim()) ||
      (apiTitle && String(apiTitle).trim()) ||
      getTranslation("newApi");
    setOpenedTabs(prev =>
      prev.map(t =>
        t.id === tempId
          ? { ...t, id: created.apiId, title: safeTitle, isTemporary: false }
          : t
      )
    );
  };

  // Kaydedildikten sonra ortak state güncellemesi
  const syncStateAfterCreate = (createdResponse, formattedResponse) => {
    const safeTitle =
      (createdResponse?.apiTitle && String(createdResponse.apiTitle).trim()) ||
      (apiTitle && String(apiTitle).trim()) ||
      getTranslation("newApi");
    setSelectedApiId(createdResponse.apiId);
    setSelectedSubMenu(createdResponse.apiId);
    setModifiedApis(prev => ({
      ...prev,
      [createdResponse.apiId]: {
        ...createdResponse,
        apiTitle: safeTitle,
        method: createdResponse.method?.toLowerCase() || "get",
        url: createdResponse.url || "",
        headers: createdResponse.headers || [],
        auth: createdResponse.auth || { type: "no-auth" },
        body: createdResponse.body ?? {},
        collectionId: createdResponse.collectionId || selectedCollectionId,
      }
    }));
    setResult(formattedResponse ?? resultsByApi[createdResponse.apiId] ?? "");
    setResultsByApi(prev => ({
      ...prev,
      [createdResponse.apiId]: formattedResponse ?? prev[createdResponse.apiId],
    }));
    setApiTitle(safeTitle);
    setSelectedMethod(createdResponse.method?.toLowerCase() || "get");
    setUrl(createdResponse.url || "");
    setHeaders(createdResponse.headers || []);
    setSelectedAuthOption(createdResponse.auth?.type || "no-auth");
    handleBearerTokenChange(createdResponse.auth?.token || "");
    setBasicAuth(createdResponse.auth?.credentials || { username: "", password: "" });
    setCode(getCodeValue(createdResponse.body));
    setIsNewApi(false);
    setSelectedNewApi(null);
  };

  // Unsaved hesaplama (tab üzerinde nokta göstermek için)
  const isTabUnsaved = (tab) => {
    if (tab.isTemporary) return true;
    const current = modifiedApis[tab.id];
    const original = originalApiMap[tab.id];
    return current ? isApiDirty(current, original) : false;
  };
  const handleFolderClick = (folderId) => {
    setSelectedFolderId(folderId);
  };
  const formatDuration = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const centis = Math.floor((ms % 1000) / 10);
    return `${minutes}:${seconds.toString().padStart(2, "0")}.${centis
      .toString()
      .padStart(2, "0")}`;
  };
  const methodOptions = [
    { value: "get", label: "GET" },
    { value: "post", label: "POST" },
    { value: "put", label: "PUT" },
    { value: "delete", label: "DELETE" },
  ];
  const originalApiMap = useMemo(() => {
    const map = {};
    collections.forEach((collection) => {
      if (collection.ungroup && Array.isArray(collection.ungroup)) {
        collection.ungroup.forEach((api) => {
          map[api.apiId] = api;
        });
      }
      if (collection.groups && Array.isArray(collection.groups)) {
        collection.groups.forEach((group) => {
          if (group.apis && Array.isArray(group.apis)) {
            group.apis.forEach((api) => {
              map[api.apiId] = api;
            });
          }
        });
      }
    });
    return map;
  }, [collections]);
  useEffect(() => {
    if (!originalApiMap) return;
    setModifiedApis(prev => {
      const next = { ...prev };
      Object.keys(prev).forEach((id) => {
        const original = originalApiMap[id];
        if (original && !isApiDirty(prev[id], original)) {
          delete next[id];
        }
      });
      return next;
    });
  }, [originalApiMap]);



  // Şu an seçili olan API hangi klasörde (group) duruyor?
  const getCurrentGroupId = () => {
    // Eğer temp API ise ve groupId bilgisi varsa onu kullan
    if (selectedNewApi?.isTemporary && selectedNewApi?.groupId) {
      return selectedNewApi.groupId;
    }

    if (!selectedCollectionId || !selectedApiId) return null;

    const collection = collections.find(
      (c) => c.collectionId === selectedCollectionId
    );
    if (!collection || !Array.isArray(collection.groups)) return null;

    // Bu koleksiyondaki gruplar içinde, şu anki API'yi içeren grubu bul
    const group = collection.groups.find((g) =>
      (g.apis || []).some((api) => api.apiId === selectedApiId)
    );

    return group?.groupId ?? null;
  };

  const handleMoveToGroup = (group) => {
    if (!selectedApiId) return;
    if (selectedNewApi?.isTemporary) {
      setTemporaryApis((prev) =>
        prev.map((api) =>
          api.apiId === selectedApiId ? { ...api, groupId: group.groupId } : api
        )
      );
    } else {
      handleChangeApiGroup(selectedCollectionId, selectedApiId, group.groupId);
    }
    setGroupMenuOpen(false);
  };
  const transformCollectionsToMenus = (collections) => {
    if (!collections || collections.length === 0) {
      return [];
    }
    return collections.map((collection) => {
      const tempApisForCollection = temporaryApis.filter(
        (api) => api.collectionId === collection.collectionId
      );
      const savedUngroupApis = collection.ungroup || [];
      const filteredTempUngroupApis = tempApisForCollection.filter(
        (tempApi) => !savedUngroupApis.some((api) => api.apiId === tempApi.apiId)
      );
      const allUngroupApis = [...savedUngroupApis, ...filteredTempUngroupApis];

      const ungroupItems = allUngroupApis.map((api) => {
        const originalApi = originalApiMap[api.apiId];
        const unsaved =
          api.isTemporary ||
          (!api.isTemporary &&
            modifiedApis[api.apiId] &&
            isApiDirty(modifiedApis[api.apiId], originalApi));
        return {
          title: api.apiTitle,
          id: api.apiId,
          method: api.method,
          url: api?.url,
          auth: api.auth,
          headers: api.headers,
          body: api.body,
          collectionId: collection.collectionId,
          isTemporary: api.isTemporary || false,
          unsaved,
        };
      });

      const groupMenus =
        collection.groups && collection.groups.length > 0
          ? collection.groups.map((group) => {
            const savedGroupApis = group.apis || [];
            const tempApisForGroup = temporaryApis.filter(
              (api) => api.groupId === group.groupId
            );
            const filteredTempGroupApis = tempApisForGroup.filter(
              (tempApi) =>
                !savedGroupApis.some((api) => api.apiId === tempApi.apiId)
            );
            const allGroupApis = [...savedGroupApis, ...filteredTempGroupApis];
            return {
              title: group.title,
              id: group.groupId,
              icon: <TbFolder size={18} />,
              nestedSubmenu: allGroupApis.map((api) => {
                const originalApi = originalApiMap[api.apiId];
                const unsaved =
                  api.isTemporary ||
                  (!api.isTemporary &&
                    modifiedApis[api.apiId] &&
                    isApiDirty(modifiedApis[api.apiId], originalApi));
                return {
                  title: api.apiTitle,
                  id: api.apiId,
                  method: api.method,
                  url: api?.url,
                  auth: api.auth,
                  headers: api.headers,
                  body: api.body,
                  collectionId: collection.collectionId,
                  isTemporary: api.isTemporary || false,
                  unsaved,
                };
              }),
            };
          })
          : [];

      const submenuItems = [];
      if (ungroupItems.length > 0) submenuItems.push(...ungroupItems);
      if (groupMenus.length > 0) submenuItems.push(...groupMenus);

      return {
        sections: [
          {
            title: collection.collectionTitle,
            id: collection.collectionId,
            icon: <TbFolder size={20} />,
            submenu: submenuItems,
          },
        ],
      };
    });
  };

  const normalizeBody = (body) => {
    try {
      if (typeof body === "string") {
        return JSON.stringify(JSON.parse(body));
      }
      return JSON.stringify(body);
    } catch (error) {
      return body;
    }
  };
  const isApiDirty = (currentApi, originalApi) => {
    if (!originalApi) return false;
    const normalizeAndTreatEmptyAsNull = (body) => {
      const normalized = normalizeBody(body);
      return normalized === "{}" || normalized === "[]" ? "null" : normalized;
    };
    if (currentApi.apiTitle) {
      return (
        currentApi.apiTitle !== originalApi.apiTitle ||
        currentApi.url !== originalApi.url ||
        currentApi.method?.toLowerCase() !== originalApi.method?.toLowerCase() ||
        normalizeAndTreatEmptyAsNull(currentApi.body) !== normalizeAndTreatEmptyAsNull(originalApi.body) ||
        JSON.stringify(currentApi.auth) !== JSON.stringify(originalApi.auth) ||
        JSON.stringify(currentApi.headers) !== JSON.stringify(originalApi.headers)
      );
    }
    else {
      return false
    }
  };
  const menus = useMemo(
    () => transformCollectionsToMenus(collections),
    [collections, temporaryApis, modifiedApis]
  );
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleOpenDocumentation = () => {
    const docId =
      selectedApiId ||
      selectedNewApi?.apiId ||
      selectedApi?.apiId ||
      selectedApi?.id;
    if (!docId) return;
    setSelectedDocumentationID(docId);
    handleGetDocumentation(docId);
    openModal("apiDocs");
  };

  const sendAPIRequest = async (
    method,
    url,
    headers,
    body,
    authType,
    basicAuth,
    signal
  ) => {
    try {
      let authHeaders = {};
      if (authType === "basic-auth" && basicAuth.username && basicAuth.password) {
        const encodedCredentials = btoa(`${basicAuth.username}:${basicAuth.password}`);
        authHeaders = { Authorization: `Basic ${encodedCredentials}` };
      }
      if (authType === "Bearer" && bearerToken) {
        authHeaders = { Authorization: `Bearer ${bearerToken}` };
      }
      const filteredHeaders = headers.reduce((acc, header) => {
        const key = header.key?.trim();
        const value = header.value?.trim();
        if (key && value) acc[key] = value;
        return acc;
      }, {});

      // Remove comments from body string before parsing/sending
      const processedBody = removeComments(body);

      const options = {
        method,
        url,
        headers: { ...filteredHeaders, ...authHeaders },
        ...(method === "GET"
          ? { params: processedBody && typeof processedBody === "object" ? processedBody : JSON.parse(processedBody) }
          : { data: processedBody && typeof processedBody === "object" ? processedBody : JSON.parse(processedBody) }),
        signal,
      };
      const response = await axios(options);
      return response.data;
    } catch (error) {
      if (axios.isCancel?.(error) || error?.name === "CanceledError" || error?.message === "canceled") {
        throw new Error("__REQUEST_CANCELED__");
      }
      throw error;
    }
  };

  const getCodeValue = (body) => {
    return body && typeof body === "string"
      ? body
      : JSON.stringify(body || {}, null, 2);
  };

  const handleSend = async () => {
    if (!isValidUrl(url)) {
      toastWarning(getTranslation("pleaseEnterAValidURL"));
      return;
    }
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { }
    }
    abortRef.current = new AbortController();
    if (timerRef.current) clearInterval(timerRef.current);
    startRef.current = Date.now();
    setElapsedMs(0);
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - startRef.current), 50);
    setIsLoading(true);
    handleTabClick("body");

    // Remove comments from the current code before sending
    const processedCodeForSend = removeComments(code);

    let authObj = {};
    if (selectedAuthOption === "basic-auth") authObj = { type: selectedAuthOption, credentials: basicAuth };
    else if (selectedAuthOption === "Bearer") authObj = { type: "Bearer", token: bearerToken };
    else authObj = { type: "no-auth" };
    let newApi = {
      apiTitle, headers, method: selectedMethod, url,
      // Use the processed code (comments removed) for the request
      body: processedCodeForSend ? processedCodeForSend : {},
      auth: authObj,
      baseUrl: getBaseUrl(url), endpoint: getEndPoint(url),
      documentationName: apiTitle,
    };
    try {
      const response = await sendAPIRequest(
        newApi.method,
        newApi.url,
        newApi.headers,
        // Pass the processed body string
        newApi.body,
        selectedAuthOption,
        basicAuth,
        abortRef.current.signal
      );
      const formattedResponse = JSON.stringify(response, null, 2);
      if (!isNewApi && selectedApiId) {
        setResult(formattedResponse);
        setResultsByApi(prev => ({ ...prev, [selectedApiId]: formattedResponse }));
      } else {
        const createdResponse = await handleAddApi(
          newApi,
          formattedResponse,
          selectedCollectionId || requiredCollectionIdForCreateApi
        );
        if (createdResponse) {
          setTemporaryApis(prev =>
            prev.filter(api => api.apiId !== selectedNewApi?.apiId && api.id !== selectedNewApi?.apiId)
          );
          promoteTempTab((selectedNewApi?.apiId || selectedNewApi?.id), createdResponse);
          syncStateAfterCreate(createdResponse, formattedResponse);
        }
      }
      newApi.response = formattedResponse;
    } catch (error) {
      if (error?.message === "__REQUEST_CANCELED__") {
        const canceledText = "/*" + getTranslation("requestCanceledByUser") + "*/";
        setResult(canceledText);
        if (selectedApiId) {
          setResultsByApi(prev => ({ ...prev, [selectedApiId]: canceledText }));
        }
      } else {
        const formattedResponse = JSON.stringify(error, null, 2);
        setResult(formattedResponse);
        if (selectedApiId) {
          setResultsByApi(prev => ({ ...prev, [selectedApiId]: formattedResponse }));
        }
        console.error("API isteği hatası:", error);
      }
    } finally {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setElapsedMs(Date.now() - startRef.current);
      setIsLoading(false);
      abortRef.current = null;
    }
  };

  const handleCancelRequest = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { }
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setElapsedMs(Date.now() - startRef.current);
    setIsLoading(false);
  };
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) { }
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Tüm apiTitle'ları topla (koleksiyonlar + gruplar + temp + modified)
  // Tüm apiTitle'ları topla (koleksiyonlar + gruplar + temp + modified + açık sekmeler + mevcut başlık)
  const getAllApiTitles = () => {
    const titles = new Set();

    // collections içindekiler
    collections.forEach((collection) => {
      (collection.ungroup || []).forEach((api) => {
        if (api.apiTitle) titles.add(api.apiTitle);
      });

      (collection.groups || []).forEach((group) => {
        (group.apis || []).forEach((api) => {
          if (api.apiTitle) titles.add(api.apiTitle);
        });
      });
    });

    // temporaryApis içindekiler
    temporaryApis.forEach((api) => {
      if (api.apiTitle) titles.add(api.apiTitle);
    });

    // modifiedApis içindekiler
    Object.values(modifiedApis || {}).forEach((api) => {
      if (api?.apiTitle) titles.add(api.apiTitle);
    });

    // Açık sekmelerde görünen başlıklar
    openedTabs.forEach((tab) => {
      if (tab.title) {
        titles.add(tab.title);
      }
    });

    // Şu an ekranda görünen başlık
    if (apiTitle && apiTitle.trim()) {
      titles.add(apiTitle.trim());
    }

    return Array.from(titles);
  };
  const getNextCopyTitle = (currentTitle) => {
    const allTitles = getAllApiTitles();

    // "test" veya "test (1)" gibi başlıklardan base title'ı çıkar
    const baseMatch = currentTitle.match(/^(.*?)(?:\s\((\d+)\))?$/);
    const baseTitle = (baseMatch?.[1] || currentTitle).trim();

    // Eğer base title hiç yoksa direkt onu kullan
    if (!allTitles.includes(baseTitle)) {
      return baseTitle;
    }

    let maxIndex = 0;
    const prefix = baseTitle + " ("; // örn: "test ("

    allTitles.forEach((title) => {
      // "test (1)" gibi olanları yakala
      if (title.startsWith(prefix) && title.endsWith(")")) {
        const inside = title.slice(prefix.length, -1); // parantez içi
        const num = parseInt(inside, 10);
        if (!Number.isNaN(num) && num > maxIndex) {
          maxIndex = num;
        }
      }
    });


    // test      -> test (1)
    // test (1)  -> test (2)
    // test (2)  -> test (3)
    return `${baseTitle} (${maxIndex + 1})`;
  };


  const handleSave = async (type) => {
    if (!isValidUrl(url)) {
      toastWarning(getTranslation("pleaseEnterAValidURL"));
      return;
    }
    let authObj = {};
    if (selectedAuthOption === "basic-auth") {
      authObj = {
        type: selectedAuthOption,
        credentials: basicAuth,
      };
    } else if (selectedAuthOption === "Bearer") {
      authObj = {
        type: "Bearer",
        token: bearerToken,
      };
    } else {
      authObj = {
        type: "no-auth",
      };
    }
    // For saving, use the raw code (with comments) as the user intended
    let updatedApi = {
      apiTitle: apiTitle,
      headers: headers,
      method: selectedMethod,
      url: url,
      // Use the original code with comments for saving
      body: code,
      auth: authObj,
      baseUrl: getBaseUrl(url),
      endpoint: getEndPoint(url),
      documentationName: apiTitle,
      response: result,
      collectionId: selectedCollectionId,
    };
    if (type === "save") {
      if (isNewApi) {
        const createdResponse = await handleAddApi(
          updatedApi,
          updatedApi.response,
          selectedCollectionId
        );
        if (createdResponse) {
          setTemporaryApis((prev) =>
            prev.filter(
              (api) =>
                api.apiId !== selectedNewApi?.apiId &&
                api.id !== selectedNewApi?.apiId
            )
          );
          promoteTempTab((selectedNewApi?.apiId || selectedNewApi?.id), createdResponse);
          syncStateAfterCreate(createdResponse, updatedApi.response);
        }
      } else {
        handleUpdateApi(selectedApiId, updatedApi, selectedCollectionId);
        setModifiedApis(prev => {
          const { [selectedApiId]: _, ...rest } = prev;
          return rest;
        });
      }
      if (!isNewApi) {
        setHeaders(updatedApi.headers);
        setSelectedMethod(updatedApi.method);
        setUrl(updatedApi?.url);
        setApiTitle(updatedApi.apiTitle);
        setCode(updatedApi.body);
        setSelectedAuthOption(updatedApi.auth.type);
        if (updatedApi.auth.type === "basic-auth") {
          setBasicAuth(updatedApi.auth.credentials);
        }
        setResult(updatedApi.response);
      }
    } else if (type === "saveAs") {
      // 1) Base title belirle
      const baseTitle =
        (apiTitle && apiTitle.trim()) || getTranslation("newApi");

      // 2) Aynı isimlerden kaç tane olduğunu bul ve sıradaki "(n)" ismini üret
      const newTitle = getNextCopyTitle(baseTitle);

      // 3) Save As için kullanılacak kopya obje
      const updatedApiForSaveAs = {
        ...updatedApi,
        apiTitle: newTitle,
        documentationName: newTitle,
      };

      // 4) Eski API hangi klasörde? (groupId)
      const currentGroupId = getCurrentGroupId();

      // 5) Save As ile klon API oluştur
      const createdResponse = await handleSaveAsApi(
        updatedApiForSaveAs,
        updatedApiForSaveAs.response,
        selectedCollectionId
      );

      // Eğer oluşturma başarısızsa devam etmeye gerek yok
      if (!createdResponse) return;

      // Backend farklı bir title döndürse bile biz newTitle'ı UI'da zorlarız
      const createdWithTitle = {
        ...createdResponse,
        apiTitle: newTitle,
      };

      // 6) Eğer save as işlemi bir klasör içindeyken yapıldıysa,
      // yeni API'yi de aynı klasöre move et (sanki MoveToGroup yapmışsın gibi)
      if (currentGroupId) {
        await handleChangeApiGroup(
          selectedCollectionId,
          createdWithTitle.apiId,
          currentGroupId
        );
      }

      // 7) UI tarafını güncelle: tab aç + state sync
      openTabForApi({
        ...createdWithTitle,
        isTemporary: false,
        groupId: currentGroupId || createdWithTitle.groupId || null,
        collectionId: selectedCollectionId,
      });

      syncStateAfterCreate(
        {
          ...createdWithTitle,
          groupId: currentGroupId || createdWithTitle.groupId || null,
          collectionId: selectedCollectionId,
        },
        createdWithTitle.response || updatedApiForSaveAs.response
      );
    }
  };
  const handleCreatCollection = (title) => {
    handleAddNewCollection(title);
  };
  const handleCreateAPIGroup = (title) => {
    handleCreateApiGroup(title, selectedProjectID, requiredCollectionIdForCreateGroup)
  }
  const handleCreateRequest = (title) => {
    if (!requiredCollectionIdForCreateApi) {
      console.error("Collection not selected!");
      return;
    }
    const newRequest = {
      apiId: Date.now().toString(),
      isTemporary: true,
      apiTitle: title || getTranslation("newRequest"),
      method: "get",
      url: "https://",
      auth: { type: "no-auth" },
      headers: [],
      body: {},
      collectionId: requiredCollectionIdForCreateApi,
    };
    setTemporaryApis((prev) => [...prev, newRequest]);
    handleSubmenuClick(newRequest);
  };
  const clearStates = () => {
    setApiTitle("");
    setSelectedMethod("get");
    setUrl("");
    setHeaders([]);
    setSelectedAuthOption("no-auth");
    setBasicAuth({ username: "", password: "" });
    setCode(JSON.stringify({}, null, 2));
    setSelectedCollectionId("");
    setSelectedApi({});
    setSelectedApiId("");
    setSelectedSubMenu("");
    setMenuOpen(false);
    setSelectedFolderId(null)
  };
  const handleSubmenuClick = (api) => {
    const id = api.apiId || api.id;
    setElapsedMs(0)
    openTabForApi(api);
    if (api?.isTemporary) {
      setIsNewApi(true);
      const updatedTempApi = temporaryApis.find((temp) => temp.apiId === id);
      const apiToUse = updatedTempApi || api;
      setResult(resultsByApi[id] || "");
      setApiTitle(
        apiToUse.title || apiToUse.apiTitle || getTranslation("newApi")
      );
      setSelectedMethod(apiToUse.method.toLowerCase());
      setUrl(apiToUse?.url || "");
      setHeaders(apiToUse.headers || []);
      setSelectedAuthOption(apiToUse.auth?.type || "no-auth");
      handleBearerTokenChange(apiToUse.auth?.token || "");
      setBasicAuth(
        apiToUse.auth?.credentials || { username: "", password: "" }
      );
      // Use the original code with comments when loading
      setCode(getCodeValue(apiToUse.body));
      setSelectedCollectionId(apiToUse.collectionId);
      setSelectedSubMenu(id);
      setSelectedApiId(id);
      setSelectedApi({});
      setSelectedNewApi(apiToUse);
    } else {
      setIsNewApi(false);
      const modified = api || modifiedApis[id];
      setResult(resultsByApi[id] || "");
      setApiTitle(
        modified.apiTitle || modified.title || getTranslation("newApi")
      );
      setSelectedMethod(modified.method?.toLowerCase());
      setUrl(modified.url || "");
      setHeaders(modified.headers || []);
      setSelectedAuthOption(modified.auth?.type || "no-auth");
      handleBearerTokenChange(modified.auth?.token || "");
      setBasicAuth(
        modified.auth?.credentials || { username: "", password: "" }
      );
      // Use the original code with comments when loading
      setCode(getCodeValue(modified.body));
      setSelectedCollectionId(modified.collectionId);
      setSelectedSubMenu(id);
      setSelectedApiId(id);
      handleSelectApi(api, api.collectionId);
      setSelectedNewApi(null);
    }
  };
  const handleDefaultTextChange = (event) => {
    const val = event.target.value;
    setApiTitle(val);
    updateTemporaryField("apiTitle", val);
    setOpenedTabs(prev => prev.map(t =>
      t.id === selectedApiId ? { ...t, title: val || getTranslation("newApi") } : t
    ));
  };
  const handleDefaultTextBlur = () => {
    setIsEditing(false);
    handleUpdateApiTitle(selectedApiId, selectedCollectionId, apiTitle);
  };
  const handleDefaultTextClick = () => {
    setIsEditing(true);
  };
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const updateTemporaryField = (field, value) => {
    if (selectedNewApi?.isTemporary) {
      setTemporaryApis((prev) =>
        prev.map((api) =>
          api.apiId === selectedNewApi.apiId || api.apiId === selectedNewApi.id
            ? { ...api, [field]: value }
            : api
        )
      );
    } else if (selectedApiId) {
      setModifiedApis((prev) => ({
        ...prev,
        [selectedApiId]: {
          ...(prev[selectedApiId] || originalApiMap[selectedApiId]),
          [field]: value,
        },
      }));
    }
  };

  const handleChangeBasicAuth = (event) => {
    const { name, value } = event.target;
    setBasicAuth((prevState) => {
      const newAuth = { ...prevState, [name]: value };
      updateTemporaryField("auth", {
        ...((selectedNewApi?.isTemporary
          ? selectedNewApi.auth
          : modifiedApis[selectedApiId]?.auth) || {}),
        credentials: newAuth,
      });
      return newAuth;
    });
  };
  const handleChangeBearerToken = (e) => {
    handleBearerTokenChange(e.target.value);
    updateTemporaryField("auth", {
      ...((selectedNewApi?.isTemporary
        ? selectedNewApi.auth
        : modifiedApis[selectedApiId]?.auth) || {}),
      token: e.target.value,
    });
  };
  const handleAuthOptionChange = (e) => {
    const newOption = e.target.value;
    setSelectedAuthOption(newOption);
    if (selectedNewApi?.isTemporary) {
      updateTemporaryField("auth", { ...selectedNewApi.auth, type: newOption });
      updateTemporaryField("selectedAuthOption", newOption);
    } else if (selectedApiId) {
      updateTemporaryField("auth", {
        ...modifiedApis[selectedApiId]?.auth,
        type: newOption,
      });
      updateTemporaryField("selectedAuthOption", newOption);
    }
  };
  const handleOpenAddCollectionModal = () => setIsAddCollectionModalOpen(true);
  const handleCloseAddCollectionModal = () =>
    setIsAddCollectionModalOpen(false);
  const handleOpenAddRequestModal = (id) => {
    setRequiredCollectionIdForCreateApi(id);
    setIsAddRequestModalOpen(true);
  };
  const handleCloseAddRequestModal = () => setIsAddRequestModalOpen(false);
  const handleOpenCreateGroupModal = (id) => {
    setRequiredCollectionIdForCreateGroup(id);
    setIsCreateGroupodalOpen(true);
  };
  const handleCloseCreateGroupModal = () => setIsCreateGroupodalOpen(false);
  const handleMenuToggle = () => {
    setMenuOpen((prev) => !prev);
    setGroupMenuOpen(false)
  };
  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setMenuOpen(false);
      setGroupMenuOpen(false);
    }
  };
  const handleApiDelete = () => {
    const closingId = selectedApiId;

    if (!closingId) return;

    // Devam eden istek varsa iptal et
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (_) { }
    }

    // Bu id'ye sahip API temporary mi?
    const isTempApi = temporaryApis.some(
      (api) => api.apiId === closingId || api.id === closingId
    );

    if (isTempApi) {
      // Sadece local temporary listeden düş
      setTemporaryApis((prev) =>
        prev.filter(
          (api) =>
            api.apiId !== closingId &&
            api.id !== closingId
        )
      );
    } else {
      // Gerçek (backend’te kayıtlı) API, servisi çağır
      handleDeleteApi(closingId);
    }

    // Sekmeyi kapat
    closeTab(closingId);
  };
  const updateHeaders = (newHeaders) => {
    setHeaders(newHeaders);
    updateTemporaryField("headers", newHeaders);
  };
  function parseIfJsonArray(variable) {
    if (typeof variable !== "string") {
      return variable;
    }
    try {
      let parsed = JSON.parse(variable);
      if (Array.isArray(parsed)) {
        return JSON.stringify(parsed, null, 2);
      }
    } catch (error) {
      return variable;
    }
    return variable;
  }

  // Update the updateCode function to use the new handleCodeChange
  const updateCode = (newCode) => {
    setCode(parseIfJsonArray(newCode));
    updateTemporaryField("body", parseIfJsonArray(newCode));
  };

  useEffect(() => {
    if (selectedNewApi?.isTemporary) {
      const updated = temporaryApis.find(
        (temp) =>
          temp.apiId === selectedNewApi.apiId ||
          temp.apiId === selectedNewApi.id
      );
      if (updated) {
        setSelectedNewApi(updated);
      }
    }
  }, [temporaryApis, selectedNewApi]);
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const fetchAuthorizations = async () => {
    const res = await getUserAuthorizations();
    if (res?.result?.length > 0) {
      const matchedProject = res?.result?.find(
        (item) => item.projectID === selectedProjectID
      );
      if (matchedProject) {
        setProjectAuthorization(matchedProject.authorizations);
      } else {
        setProjectAuthorization(null);
      }
    }
  };
  useEffect(() => {
    fetchAuthorizations();
  }, [selectedProjectID]);
  const tabsForHolder = useMemo(() => {
    const fallback = (v) => (v && String(v).trim()) || getTranslation("newApi");
    return openedTabs.map((t) => ({
      id: t.id,
      title: fallback(t.title),
      unsaved: isTabUnsaved(t),
    }));
  }, [openedTabs, modifiedApis, temporaryApis]);
  if (collections?.length > 0) {
    return (
      <div className="api-post-get-container">
        <ModuleSidebar
          contextMenuRenameOnClick={handleUpdateCollectionTitle}
          contextMenuDeleteOnClick={handleDeleteCollection}
          deleteMessage={getTranslation("collectionDeleteWarning")}
          addEntitiy={handleOpenAddRequestModal}
          addSecondaryEntity={handleOpenCreateGroupModal}
          groupContextMenuRenameOnClick={handleUpdateApiGroup}
          groupContextMenuDeleteOnClick={handleDeleteApiGroup}
          groupDeleteMessage={getTranslation('groupDeleteConfirmation')}
          onFolderClick={handleFolderClick}
          selectedFolderId={selectedFolderId}
          onRightClick={(id) => setSelectedCollectionId(id)}
          checkPermission={projectAuthorization?.authorizations === "all" || projectAuthorization?.authorizations?.collections === "all" || projectAuthorization?.authorizations?.collections?.some((auth) => auth.id === selectedCollectionId && auth.authorization !== "read")}
          renameModalTitle={
            collections.find(c => c.collectionId === selectedCollectionId)
              ?.collectionTitle || ""
          }
          title={
            <div>
              <div
                style={{
                  width: "100%",
                  flexDirection: "row",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {getTranslation("collections")}
                <TbPlus onClick={handleOpenAddCollectionModal} />


              </div>
              {/* Postman Import Butonu */}
              <button
                style={{ marginTop: '12px', width: '100%' }}
                className="action-button"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                {getTranslation("importFromPostman") || "Import Postman"}
              </button>
            </div>
          }
          onSubmenuClick={handleSubmenuClick}
          menus={menus}
          type={"api"}
          selectedSubmenu={selectedSubmenu}
          projectAuthorization={projectAuthorization}
          showSearch={true}
        />
        <div className="content">
          {selectedApiId && selectedApiId?.length >= 1 ? (
            <div className="api-post-get-inner-container">
              <TabsHolder
                tabs={tabsForHolder}
                activeId={selectedApiId}
                onTabClick={switchToTab}
                onCloseTab={closeTab}
              />
              <div className="api-post-get-row">
                {projectAuthorization?.authorizations === "all" || projectAuthorization?.authorizations?.collections === "all" || projectAuthorization?.authorizations?.collections?.some((auth) => auth.id === selectedCollectionId && auth.authorization !== "read")
                  ? (
                    <div onClick={handleDefaultTextClick}>
                      {isEditing ? (
                        <input
                          className="api-title-input"
                          type="text"
                          value={apiTitle}
                          onChange={handleDefaultTextChange}
                          onBlur={handleDefaultTextBlur}
                          autoFocus
                          maxLength={30}
                        />
                      ) : (
                        <span className="api-post-get-title">
                          {apiTitle} <TbEdit size={18} />
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="api-post-get-title">{apiTitle}</span>
                  )}
                <div
                  onClick={handleOpenDocumentation}
                  className="collection-documentation-button"
                >
                  {getTranslation("documentation")} <TbNews size={18} />
                </div>
              </div>
              <div className="api-post-get-row">
                <Dropdown
                  style={{ minWidth: "95px", width: "95px", height: "38px" }}
                  options={methodOptions}
                  onChange={(e) => {
                    setSelectedMethod(e.target.value);
                    updateTemporaryField("method", e.target.value);
                  }}
                  value={selectedMethod}
                />
                <InputField
                  placeholder={getTranslation("enterURL")}
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    updateTemporaryField("url", e.target.value);
                  }}
                />
                <SendButton onClick={handleSend} disabled={isLoading} />
                <button
                  className="action-button"
                  onClick={handleCancelRequest}
                  disabled={!isLoading}
                  title={isLoading ? "Cancel in-flight request" : "No active request"}
                  style={{ opacity: isLoading ? 1 : 0.6, cursor: isLoading ? "var(--pointer)" : "not-allowed" }}
                >
                  {getTranslation("cancel")}
                </button>
              </div>
              <div className="api-post-get-row">
                <div className="api-post-get-tab-container">
                  <div className="api-post-get-tabs">
                    <button
                      className={`option-tab ${activeTab === "authorization" ? "active" : ""
                        }`}
                      onClick={() => handleTabClick("authorization")}
                    >
                      Authorization
                    </button>
                    <button
                      className={`option-tab ${activeTab === "headers" ? "active" : ""
                        }`}
                      onClick={() => handleTabClick("headers")}
                    >
                      Headers
                    </button>
                    <button
                      className={`option-tab ${activeTab === "body" ? "active" : ""
                        }`}
                      onClick={() => handleTabClick("body")}
                    >
                      Body
                    </button>
                    <div className="api-elapsed-time">
                      <span className="elapsed-badge" title={getTranslation("requestInProgress")}>
                        {formatDuration(elapsedMs)}
                      </span>
                    </div>
                  </div>
                  <div className="api-post-get-actions" ref={menuRef}>
                    {projectAuthorization?.authorizations === "all" ||
                      projectAuthorization?.authorizations?.collections ===
                      "all" ||
                      projectAuthorization?.authorizations?.collections?.some(
                        (auth) =>
                          auth.id === selectedCollectionId &&
                          auth.authorization !== "read"
                      ) ? (
                      <>
                        {/* Save */}
                        <button
                          onClick={() => handleSave("save")}
                          className="action-button"
                        >
                          <TbDeviceFloppy
                            size={17}
                            style={{ marginRight: "8px" }}
                          />
                          {getTranslation("save")}
                        </button>

                        {/* Actions */}
                        <button
                          className="action-button"
                          onClick={handleMenuToggle}
                        >
                          {getTranslation("actions")}
                          <TbDotsVertical
                            size={17}
                            style={{ marginLeft: "8px" }}
                          />
                        </button>

                        {menuOpen && (
                          <div className="api-dropdown-menu">
                            {selectedCollectionId &&
                              collections.find((col) => col.collectionId === selectedCollectionId)?.groups.length > 0 &&
                              <div
                                className="api-menu-item"
                                onClick={() => {
                                  setGroupMenuOpen(true);
                                  setMenuOpen(false);
                                }}
                              >
                                {getTranslation("moveToGroup")}
                              </div>
                            }
                            <div
                              className="api-menu-item"
                              onClick={() => handleSave("saveAs")}
                            >
                              {getTranslation("saveAs")}
                            </div>
                            <div
                              className="api-menu-item"
                              onClick={handleApiDelete}
                            >
                              {getTranslation("delete")}
                            </div>
                          </div>
                        )}
                        {groupMenuOpen && (
                          <div className="api-dropdown-menu">
                            <div
                              className="api-menu-item"
                              onClick={() => { setGroupMenuOpen(false); setMenuOpen(true); handleChangeApiGroup(selectedCollectionId, selectedApiId, null) }}
                            >
                              {getTranslation("ungroup")}
                            </div>
                            {selectedCollectionId &&
                              collections.find((col) => col.collectionId === selectedCollectionId)?.groups?.map((group) => (
                                <div
                                  key={group.groupId}
                                  className="api-menu-item"
                                  onClick={() => handleMoveToGroup(group)}
                                >
                                  {group.title}
                                </div>
                              ))}
                            <div
                              className="api-menu-item"
                              onClick={() => { setGroupMenuOpen(false); setMenuOpen(true); }}
                            >
                              {getTranslation("back")}
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="api-post-get-bottom">
                {activeTab === "authorization" && (
                  <Authorization
                    selectedAuthOption={selectedAuthOption}
                    setSelectedAuthOption={setSelectedAuthOption}
                    basicAuth={basicAuth}
                    handleChangeBasicAuth={handleChangeBasicAuth}
                    bearerToken={bearerToken}
                    handleBearerTokenChange={handleChangeBearerToken}
                    handleAuthOptionChange={handleAuthOptionChange}
                  />
                )}
                {activeTab === "headers" && (
                  <Headers headers={headers} setHeaders={updateHeaders} />
                )}
                {activeTab === "body" && (
                  <Body
                    code={code}
                    // ApiPostGet.js içindeki updateCode fonksiyonu zaten setCode'a karşılık geliyor,
                    // ama Body componenti updateCode fonksiyonunu bekliyor.
                    // updateCode fonksiyonunu zaten tanımlı, onu kullan.
                    setCode={updateCode} // Burası önemli! setCode yerine updateCode kullanmalısın.
                    result={result}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="api-post-get-container">
              <div className="content">
                <div className="api-post-get-inner-container">
                  <span className="api-post-get-title">
                    {getTranslation("selectApi")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        <AddCollectionModal
          isOpen={isAddCollectionModalOpen}
          onClose={handleCloseAddCollectionModal}
          onCreate={handleCreatCollection}
        />
        <AddRequestModal
          isOpen={isAddRequestModalOpen}
          onClose={handleCloseAddRequestModal}
          onCreate={handleCreateRequest}
        />
        <AddApiGroupModal
          isOpen={isCreateGroupModalOpen}
          onClose={handleCloseCreateGroupModal}
          onCreate={handleCreateAPIGroup}
        />

        <input
          type="file"
          ref={fileInputRef}
          accept="application/json,.json"
          style={{ display: "none" }}
          onChange={handleImportPostmanFile}
        />
      </div>
    );
  } else {
    return (
      <div className="api-post-get-container">
        <div className="content">
          <div className="api-post-get-inner-container">
            <button
              className="action-button"
              onClick={handleOpenAddCollectionModal}
            >
              {getTranslation("addCollection")}
            </button>
            <span className="api-post-get-title">
              {getTranslation("collectionAddedInfo")}
            </span>
          </div>
        </div>
        <AddCollectionModal
          isOpen={isAddCollectionModalOpen}
          onClose={handleCloseAddCollectionModal}
          onCreate={handleCreatCollection}
        />
      </div>
    );
  }
}